import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import {
  startOfMonth, endOfMonth, startOfWeek, endOfWeek, addDays, addMonths, subMonths,
  format, isSameMonth, isSameDay
} from 'date-fns';
import {
  TbPlus, TbEdit, TbTrash, TbDeviceFloppy, TbX, TbChevronLeft, TbChevronRight, TbSearch, TbLink, TbCalendarEvent, TbLock
} from 'react-icons/tb';
import '../../../assets/css/marketing-calendar.css';
import '../../../assets/css/tags-input.css';

const STATUS_COLORS = {
  Planned: '#6b7280',
  Active: '#E66239',
  Completed: '#4caf50',
  'On Hold': '#d4a017',
  Cancelled: '#9b2c2c'
};

const STATUSES_REQUIRING_REASON = ['On Hold', 'Cancelled'];

const WEEKDAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const EMPTY_FORM = {
  name: '', description: '', startDate: '', endDate: '', status: 'Planned', brand: '', owner: '', statusReason: ''
};

const toDateKey = (date) => format(date, 'yyyy-MM-dd');

const campaignCoversDay = (campaign, day) => {
  const dayKey = toDateKey(day);
  const startKey = toDateKey(new Date(campaign.startDate));
  const endKey = toDateKey(new Date(campaign.endDate));
  return dayKey >= startKey && dayKey <= endKey;
};

const MarketingCalendar = ({ pageTitle }) => {
  const [campaigns, setCampaigns] = useState([]);
  const [brands, setBrands] = useState([]);
  const [owners, setOwners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentMonth, setCurrentMonth] = useState(new Date());

  const [showFormModal, setShowFormModal] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [campaignToDelete, setCampaignToDelete] = useState(null);

  const [showDetailModal, setShowDetailModal] = useState(false);
  const [detailCampaign, setDetailCampaign] = useState(null);

  const [showDayModal, setShowDayModal] = useState(false);
  const [dayModalInfo, setDayModalInfo] = useState({ date: null, campaigns: [] });

  const [editingDayKey, setEditingDayKey] = useState(null);
  const [creativeSearch, setCreativeSearch] = useState('');
  const [availableCreatives, setAvailableCreatives] = useState([]);
  const searchTimeoutRef = useRef(null);

  const [notification, setNotification] = useState({ show: false, message: '', type: '' });

  const showNotification = (message, type = 'success') => {
    setNotification({ show: true, message, type });
    setTimeout(() => setNotification({ show: false, message: '', type: '' }), 4000);
  };

  const authHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
  });

  useEffect(() => {
    fetchCampaigns();
    fetchBrands();
    fetchOwners();
  }, []);

  const fetchCampaigns = async () => {
    try {
      const response = await axios.get('/api/campaigns/list', authHeaders());
      setCampaigns(response.data);
    } catch (error) {
      console.error('Error fetching campaigns:', error);
      showNotification(error.response?.data?.error || 'Failed to fetch campaigns', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchBrands = async () => {
    try {
      const response = await axios.get('/brand/list', authHeaders());
      setBrands(response.data);
    } catch (error) {
      console.error('Error fetching brands:', error);
    }
  };

  const fetchOwners = async () => {
    try {
      const response = await axios.get('/api/campaigns/owners', authHeaders());
      setOwners(response.data);
    } catch (error) {
      console.error('Error fetching owners:', error);
    }
  };

  const openCreateModal = (presetDate) => {
    setEditingCampaign(null);
    setFormData({
      ...EMPTY_FORM,
      startDate: presetDate ? toDateKey(presetDate) : '',
      endDate: presetDate ? toDateKey(presetDate) : ''
    });
    setShowFormModal(true);
  };

  const openEditModal = (campaign) => {
    setEditingCampaign(campaign);
    setFormData({
      name: campaign.name,
      description: campaign.description || '',
      startDate: toDateKey(new Date(campaign.startDate)),
      endDate: toDateKey(new Date(campaign.endDate)),
      status: campaign.status,
      brand: campaign.brand?._id || campaign.brand || '',
      owner: campaign.owner || '',
      statusReason: campaign.statusReason || ''
    });
    setShowFormModal(true);
  };

  const handleSaveCampaign = async () => {
    if (!formData.name.trim() || !formData.startDate || !formData.endDate) {
      showNotification('Name, start date and end date are required', 'error');
      return;
    }

    if (formData.endDate < formData.startDate) {
      showNotification('End date cannot be before start date', 'error');
      return;
    }

    if (STATUSES_REQUIRING_REASON.includes(formData.status) && !formData.statusReason.trim()) {
      showNotification(`Please provide a reason for status "${formData.status}"`, 'error');
      return;
    }

    try {
      const payload = {
        name: formData.name,
        description: formData.description,
        startDate: formData.startDate,
        endDate: formData.endDate,
        status: formData.status,
        statusReason: STATUSES_REQUIRING_REASON.includes(formData.status) ? formData.statusReason.trim() : '',
        owner: formData.owner,
        ...(formData.brand ? { brand: formData.brand } : {})
      };

      if (editingCampaign) {
        await axios.put(`/api/campaigns/${editingCampaign._id}`, payload, authHeaders());
        showNotification('Campaign updated successfully', 'success');
      } else {
        await axios.post('/api/campaigns/create', payload, authHeaders());
        showNotification('Campaign created successfully', 'success');
      }

      setShowFormModal(false);
      setEditingCampaign(null);
      fetchCampaigns();
    } catch (error) {
      showNotification(error.response?.data?.error || 'Failed to save campaign', 'error');
    }
  };

  const openDeleteModal = (campaign) => {
    setCampaignToDelete(campaign);
    setShowDeleteModal(true);
  };

  const confirmDeleteCampaign = async () => {
    if (!campaignToDelete) return;
    try {
      await axios.delete(`/api/campaigns/${campaignToDelete._id}`, authHeaders());
      showNotification('Campaign deleted successfully', 'success');
      setShowDeleteModal(false);
      setCampaignToDelete(null);
      setShowDetailModal(false);
      fetchCampaigns();
    } catch (error) {
      showNotification(error.response?.data?.error || 'Failed to delete campaign', 'error');
    }
  };

  const openDetailModal = async (campaign) => {
    try {
      const response = await axios.get(`/api/campaigns/${campaign._id}`, authHeaders());
      setDetailCampaign(response.data);
      setEditingDayKey(null);
      setCreativeSearch('');
      setAvailableCreatives([]);
      setShowDetailModal(true);
    } catch (error) {
      showNotification(error.response?.data?.error || 'Failed to load campaign', 'error');
    }
  };

  const refreshDetailCampaign = async (campaignId) => {
    const response = await axios.get(`/api/campaigns/${campaignId}`, authHeaders());
    setDetailCampaign(response.data);
    fetchCampaigns();
  };

  const getCampaignDays = (campaign) => {
    if (!campaign) return [];
    const result = [];
    let cursor = new Date(campaign.startDate);
    const end = new Date(campaign.endDate);
    while (toDateKey(cursor) <= toDateKey(end)) {
      result.push(cursor);
      cursor = addDays(cursor, 1);
    }
    return result;
  };

  const getCreativesForDay = (dayKey) =>
    detailCampaign?.dailyCreatives
      ?.filter((entry) => entry.date === dayKey)
      .map((entry) => entry.creative)
      .filter(Boolean) || [];

  const openDaySchedulePicker = (dayKey) => {
    setEditingDayKey((prev) => (prev === dayKey ? null : dayKey));
    setCreativeSearch('');
    setAvailableCreatives([]);
  };

  const closeDaySchedulePicker = () => {
    setEditingDayKey(null);
    setCreativeSearch('');
    setAvailableCreatives([]);
  };

  const handleSearchCreatives = (value) => {
    setCreativeSearch(value);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    if (!detailCampaign) return;

    searchTimeoutRef.current = setTimeout(async () => {
      try {
        const response = await axios.get(
          `/api/campaigns/${detailCampaign._id}/available-creatives`,
          { ...authHeaders(), params: { search: value } }
        );
        setAvailableCreatives(response.data);
      } catch (error) {
        console.error('Error searching creatives:', error);
      }
    }, 300);
  };

  const handleAddDayCreative = async (dayKey, creativeId) => {
    try {
      await axios.post(`/api/campaigns/${detailCampaign._id}/days/${dayKey}`, { creativeId }, authHeaders());
      setCreativeSearch('');
      setAvailableCreatives([]);
      await refreshDetailCampaign(detailCampaign._id);
    } catch (error) {
      showNotification(error.response?.data?.error || 'Failed to add creative', 'error');
    }
  };

  const handleRemoveDayCreative = async (dayKey, creativeId) => {
    try {
      await axios.delete(`/api/campaigns/${detailCampaign._id}/days/${dayKey}/${creativeId}`, authHeaders());
      await refreshDetailCampaign(detailCampaign._id);
    } catch (error) {
      showNotification(error.response?.data?.error || 'Failed to remove creative', 'error');
    }
  };

  const getCampaignsForDay = (day) => campaigns.filter((c) => campaignCoversDay(c, day));

  const openDayModal = (day, dayCampaigns) => {
    setDayModalInfo({ date: day, campaigns: dayCampaigns });
    setShowDayModal(true);
  };

  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const gridStart = startOfWeek(monthStart, { weekStartsOn: 1 });
  const gridEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });

  const days = [];
  let cursor = gridStart;
  while (cursor <= gridEnd) {
    days.push(cursor);
    cursor = addDays(cursor, 1);
  }

  const today = new Date();

  if (loading) {
    return <div className="campaign-loading">Loading marketing calendar...</div>;
  }

  return (
    <div className="campaign-calendar-container">
      <div className="campaign-header">
        <div className="campaign-header-left">
          <h2>{pageTitle || 'Marketing Calendar'}</h2>
        </div>
        <div className="campaign-header-right">
          <button className="campaign-create-btn" onClick={() => openCreateModal()}>
            <TbPlus /> New Campaign
          </button>
        </div>
      </div>

      <div className="campaign-month-nav">
        <button className="campaign-nav-btn" onClick={() => setCurrentMonth((m) => subMonths(m, 1))}>
          <TbChevronLeft />
        </button>
        <h3>{format(currentMonth, 'MMMM yyyy')}</h3>
        <button className="campaign-nav-btn" onClick={() => setCurrentMonth((m) => addMonths(m, 1))}>
          <TbChevronRight />
        </button>
        <button className="campaign-today-btn" onClick={() => setCurrentMonth(new Date())}>
          Today
        </button>
      </div>

      <div className="campaign-grid-weekdays">
        {WEEKDAY_LABELS.map((label) => (
          <div key={label} className="campaign-weekday-label">{label}</div>
        ))}
      </div>

      <div className="campaign-grid">
        {days.map((day) => {
          const dayCampaigns = getCampaignsForDay(day);
          const visible = dayCampaigns.slice(0, 3);
          const overflowCount = dayCampaigns.length - visible.length;

          return (
            <div
              key={toDateKey(day)}
              className={`campaign-day-cell ${isSameMonth(day, currentMonth) ? '' : 'outside-month'} ${isSameDay(day, today) ? 'today' : ''}`}
              onDoubleClick={() => openCreateModal(day)}
              title="Double-click to create a campaign on this day"
            >
              <span className="campaign-day-number">{format(day, 'd')}</span>
              <div className="campaign-day-chips">
                {visible.map((c) => (
                  <button
                    key={c._id}
                    className="campaign-chip"
                    style={{ backgroundColor: STATUS_COLORS[c.status] }}
                    onClick={() => openDetailModal(c)}
                    title={c.name}
                  >
                    {c.name}
                  </button>
                ))}
                {overflowCount > 0 && (
                  <button
                    className="campaign-chip-more"
                    onClick={() => openDayModal(day, dayCampaigns)}
                  >
                    +{overflowCount} more
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Create/Edit Campaign Modal */}
      <AnimatePresence>
        {showFormModal && (
          <motion.div className="campaign-modal-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <motion.div className="campaign-modal-content" initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.8, opacity: 0 }}>
              <button className="campaign-modal-close" onClick={() => setShowFormModal(false)}>✕</button>
              <h3>{editingCampaign ? 'Edit Campaign' : 'New Campaign'}</h3>

              <div className="campaign-form-group">
                <label>Name</label>
                <input
                  type="text"
                  className="campaign-input"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Campaign name"
                />
              </div>

              <div className="campaign-form-group">
                <label>Description</label>
                <textarea
                  className="campaign-input"
                  rows={3}
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="What is this campaign about?"
                />
              </div>

              <div className="campaign-form-row">
                <div className="campaign-form-group">
                  <label>Start Date</label>
                  <input
                    type="date"
                    className="campaign-input"
                    value={formData.startDate}
                    onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                  />
                </div>
                <div className="campaign-form-group">
                  <label>End Date</label>
                  <input
                    type="date"
                    className="campaign-input"
                    value={formData.endDate}
                    onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                  />
                </div>
              </div>

              <div className="campaign-form-row">
                <div className="campaign-form-group">
                  <label>Status</label>
                  <select
                    className="campaign-input"
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  >
                    <option value="Planned">Planned</option>
                    <option value="Active">Active</option>
                    <option value="Completed">Completed</option>
                    <option value="On Hold">On Hold</option>
                    <option value="Cancelled">Cancelled</option>
                  </select>
                </div>
                <div className="campaign-form-group">
                  <label>Brand</label>
                  <select
                    className="campaign-input"
                    value={formData.brand}
                    onChange={(e) => setFormData({ ...formData, brand: e.target.value })}
                  >
                    <option value="">No brand</option>
                    {brands.map((brand) => (
                      <option key={brand._id} value={brand._id}>{brand.brandName}</option>
                    ))}
                  </select>
                </div>
              </div>

              {STATUSES_REQUIRING_REASON.includes(formData.status) && (
                <div className="campaign-form-group">
                  <label>Reason*</label>
                  <textarea
                    className="campaign-input"
                    rows={2}
                    value={formData.statusReason}
                    onChange={(e) => setFormData({ ...formData, statusReason: e.target.value })}
                    placeholder={`Why is this campaign "${formData.status}"?`}
                  />
                </div>
              )}

              <div className="campaign-form-group">
                <label>Owner</label>
                <select
                  className="campaign-input"
                  value={formData.owner}
                  onChange={(e) => setFormData({ ...formData, owner: e.target.value })}
                >
                  <option value="">No owner</option>
                  {owners.map((ownerUser) => (
                    <option key={ownerUser._id} value={ownerUser.username}>
                      {ownerUser.firstName} {ownerUser.lastName} ({ownerUser.username})
                    </option>
                  ))}
                </select>
              </div>

              <div className="campaign-modal-actions">
                <button className="campaign-btn-cancel" onClick={() => setShowFormModal(false)}>Cancel</button>
                <button className="campaign-btn-save" onClick={handleSaveCampaign}>
                  <TbDeviceFloppy /> {editingCampaign ? 'Update' : 'Create'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Delete Confirmation Modal */}
      <AnimatePresence>
        {showDeleteModal && (
          <motion.div className="campaign-modal-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <motion.div className="campaign-modal-content" initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.8, opacity: 0 }}>
              <button className="campaign-modal-close" onClick={() => setShowDeleteModal(false)}>✕</button>
              <h3>Delete Campaign</h3>
              <p>Are you sure you want to delete <strong>"{campaignToDelete?.name}"</strong>? This cannot be undone.</p>
              <div className="campaign-modal-actions">
                <button className="campaign-btn-cancel" onClick={() => setShowDeleteModal(false)}>Cancel</button>
                <button className="campaign-btn-delete" onClick={confirmDeleteCampaign}><TbTrash /> Delete</button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Day Overflow Modal */}
      <AnimatePresence>
        {showDayModal && (
          <motion.div className="campaign-modal-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <motion.div className="campaign-modal-content" initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.8, opacity: 0 }}>
              <button className="campaign-modal-close" onClick={() => setShowDayModal(false)}>✕</button>
              <h3>{dayModalInfo.date && format(dayModalInfo.date, 'MMMM d, yyyy')}</h3>
              <div className="campaign-day-modal-list">
                {dayModalInfo.campaigns.map((c) => (
                  <button
                    key={c._id}
                    className="campaign-day-modal-item"
                    style={{ borderLeftColor: STATUS_COLORS[c.status] }}
                    onClick={() => { setShowDayModal(false); openDetailModal(c); }}
                  >
                    {c.name}
                  </button>
                ))}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Campaign Detail Modal */}
      <AnimatePresence>
        {showDetailModal && detailCampaign && (
          <motion.div className="campaign-modal-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <motion.div className="campaign-modal-content campaign-detail-modal" initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.8, opacity: 0 }}>
              <button className="campaign-modal-close" onClick={() => setShowDetailModal(false)}>✕</button>

              <div className="campaign-detail-header">
                <h3>{detailCampaign.name}</h3>
                <span className="campaign-status-badge" style={{ backgroundColor: STATUS_COLORS[detailCampaign.status] }}>
                  {detailCampaign.status}
                </span>
              </div>

              <div className="campaign-detail-meta">
                <span>
                  {format(new Date(detailCampaign.startDate), 'MMM d, yyyy')} – {format(new Date(detailCampaign.endDate), 'MMM d, yyyy')}
                </span>
                {detailCampaign.brand?.brandName && <span>Brand: {detailCampaign.brand.brandName}</span>}
                {detailCampaign.owner && <span>Owner: {detailCampaign.owner}</span>}
              </div>

              {detailCampaign.description && (
                <p className="campaign-detail-description">{detailCampaign.description}</p>
              )}

              {STATUSES_REQUIRING_REASON.includes(detailCampaign.status) && detailCampaign.statusReason && (
                <p className="campaign-status-reason">
                  <strong>Reason for "{detailCampaign.status}":</strong> {detailCampaign.statusReason}
                </p>
              )}

              <div className="campaign-detail-actions">
                <button className="campaign-btn-cancel" onClick={() => { setShowDetailModal(false); openEditModal(detailCampaign); }}>
                  <TbEdit /> Edit
                </button>
                <button className="campaign-btn-delete" onClick={() => openDeleteModal(detailCampaign)}>
                  <TbTrash /> Delete
                </button>
              </div>

              <div className="campaign-daily-schedule">
                <h4><TbCalendarEvent /> Daily Schedule</h4>

                <div className="campaign-schedule-list">
                  {getCampaignDays(detailCampaign).map((day) => {
                    const dayKey = toDateKey(day);
                    const dayCreatives = getCreativesForDay(dayKey);
                    const isEditing = editingDayKey === dayKey;
                    const isLocked = dayKey <= toDateKey(today);

                    return (
                      <div key={dayKey} className={`campaign-schedule-row ${isLocked ? 'locked' : ''}`}>
                        <div className="campaign-schedule-date">
                          <span className="campaign-schedule-day-label">{format(day, 'EEE, MMM d')}</span>
                          {isLocked && <TbLock className="campaign-schedule-lock-icon" title="This day has passed" />}
                        </div>

                        <div className="campaign-schedule-creative">
                          {dayCreatives.length > 0 ? (
                            <div className="campaign-schedule-creative-list">
                              {dayCreatives.map((creative) => (
                                <div key={creative._id} className="campaign-linked-item">
                                  <div className="campaign-linked-item-info">
                                    <span className="campaign-linked-item-title">{creative.title}</span>
                                    <div className="campaign-linked-item-badges">
                                      {creative.creative && (
                                        <span className="campaign-type-badge">{creative.creative}</span>
                                      )}
                                      {creative.tags?.map((tag, idx) => (
                                        <span key={idx} className="tag-chip tag-chip-sm">{tag}</span>
                                      ))}
                                    </div>
                                  </div>
                                  {!isLocked && (
                                    <button
                                      className="campaign-unlink-btn"
                                      title="Remove from this day"
                                      onClick={() => handleRemoveDayCreative(dayKey, creative._id)}
                                    >
                                      <TbX />
                                    </button>
                                  )}
                                </div>
                              ))}
                            </div>
                          ) : (
                            <span className="campaign-no-linked">No creatives assigned</span>
                          )}
                        </div>

                        {!isLocked && (
                          <div className="campaign-schedule-actions">
                            <button
                              className="campaign-btn-cancel campaign-schedule-change-btn"
                              onClick={() => (isEditing ? closeDaySchedulePicker() : openDaySchedulePicker(dayKey))}
                            >
                              {isEditing ? <><TbX /> Done</> : <><TbPlus /> Add</>}
                            </button>
                          </div>
                        )}

                        {!isLocked && isEditing && (
                          <div className="campaign-schedule-picker">
                            <div className="campaign-search-input-wrapper">
                              <TbSearch className="campaign-search-icon" />
                              <input
                                type="text"
                                className="campaign-input campaign-search-input"
                                placeholder="Search creatives by title or tag..."
                                value={creativeSearch}
                                onChange={(e) => handleSearchCreatives(e.target.value)}
                                autoFocus
                              />
                            </div>

                            {availableCreatives.length > 0 && (
                              <div className="campaign-search-results">
                                {availableCreatives.map((creative) => (
                                  <button
                                    key={creative._id}
                                    className="campaign-search-result-item"
                                    onClick={() => handleAddDayCreative(dayKey, creative._id)}
                                  >
                                    <TbLink />
                                    <span>{creative.title}</span>
                                    {creative.creative && <span className="campaign-type-badge">{creative.creative}</span>}
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Notification */}
      <AnimatePresence>
        {notification.show && (
          <motion.div
            className={`campaign-notification ${notification.type}`}
            initial={{ x: 300, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: 300, opacity: 0 }}
          >
            {notification.message}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default MarketingCalendar;
